import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormContext } from "react-hook-form";
import Accordion from "../../../Containers/Accordion";
import ChipOptional from "../../../Chip/ChipOptional";
import {
  ADS_CAMPAIGN_LANGUAGE_PLACEHOLDER,
  ADS_CAMPAIGN_LOCATION_PLACEHOLDER,
  ADS_CAMPAIGN_PLATFORMS_PLACEHOLDER,
  CREATE_A_CAMPAIGN,
  CREATE_A_CAMPAIGN_FOR_BRAND_NAME,
  CREATE_CAMPAIGN_WIZARD_STEP_INSTRUCTIONS_HELP,
  TITLE_CAMPAIGN_INSTRUCTIONS,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BOX_SHADOW_FIELDS,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";
import {
  CREATE_CAMPAIGN_SECTION_HELPER_SX,
  CREATE_CAMPAIGN_SECTION_TITLE_SX,
} from "../createAdsCampaignWizardConstants";
import { canSelectCreateCampaignPlatform } from "../createAdsCampaignWizardUtils";
import StepInstructions from "./StepInstructions";
import StepLanguage from "./StepLanguage";
import StepLocations from "./StepLocations";
import StepPlatform from "./StepPlatform";

const SETUP_BOX_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 1.5,
  width: "100%",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  borderRadius: 3,
  p: { xs: 2, sm: 2.5 },
  backgroundColor: "card.background",
};

const FieldGroup = ({ title, children, sx = {} }) => {
  return (
    <Box sx={{ ...SETUP_BOX_SX, ...sx }}>
      {title ? (
        <Typography variant="subtitle1" sx={CREATE_CAMPAIGN_SECTION_TITLE_SX}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
};

const CreateCampaignSetup = ({
  disabled = false,
  error = null,
  businessName = "",
  onSelectPlatform = () => {},
}) => {
  const { t } = useTranslationApp();
  const { watch } = useFormContext();
  const context = watch("context");
  const language = watch("language");
  const locations = watch("locations");
  const attachmentsFiles = watch("attachmentsFiles") || [];
  const hasThemeContent =
    Boolean(String(context || "").trim()) || attachmentsFiles.length > 0;
  const canSelectPlatform = canSelectCreateCampaignPlatform({
    language,
    locations,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5 },
        width: "100%",
        pb: 1,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, mt: 1 }}>
        {t(
          businessName ? CREATE_A_CAMPAIGN_FOR_BRAND_NAME : CREATE_A_CAMPAIGN,
          { value: businessName }
        )}
      </Typography>

      {error ? (
        <Alert
          severity="error"
          sx={{
            width: "100%",
            backgroundColor: "fields.background",
            color: "background.paperContrastText",
            border: `1px solid ${INPUT_COLOR_BORDER}`,
            borderRadius: 3,
            boxShadow: BOX_SHADOW_FIELDS,
          }}
        >
          {error}
        </Alert>
      ) : null}

      <FieldGroup title={t(ADS_CAMPAIGN_LANGUAGE_PLACEHOLDER)}>
        <StepLanguage disabled={disabled} />
      </FieldGroup>

      <FieldGroup title={t(ADS_CAMPAIGN_LOCATION_PLACEHOLDER)}>
        <StepLocations />
      </FieldGroup>

      <FieldGroup>
        <Accordion
          id="create-campaign-theme"
          title={t(TITLE_CAMPAIGN_INSTRUCTIONS)}
          titleAlign="left"
          hasBorder={false}
          actionsComponent={<ChipOptional size="small" />}
          initialValue={hasThemeContent}
          variantTitle="subtitle1"
          minHeight="auto !important"
          sxTitle={CREATE_CAMPAIGN_SECTION_TITLE_SX}
          sxContainerTitle={{
            px: 0,
            minHeight: "auto !important",
          }}
          sx={{
            width: "100%",
            borderRadius: "0 !important",
            backgroundColor: "transparent",
            "& .MuiAccordionDetails-root": {
              padding: "16px 0 0",
            },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, ...CREATE_CAMPAIGN_SECTION_HELPER_SX }}
          >
            {t(CREATE_CAMPAIGN_WIZARD_STEP_INSTRUCTIONS_HELP)}
          </Typography>
          <StepInstructions disabled={disabled} />
        </Accordion>
      </FieldGroup>

      {canSelectPlatform ? (
        <FieldGroup title={t(ADS_CAMPAIGN_PLATFORMS_PLACEHOLDER)}>
          <StepPlatform
            disabled={disabled}
            onSelectPlatform={onSelectPlatform}
          />
        </FieldGroup>
      ) : null}
    </Box>
  );
};

export default CreateCampaignSetup;
